import type { ReactNode } from 'react';

import { TeamUserAvatar } from '../Avatar';
import { General, Users } from '../StrokeIcons';
import type { ColorName, Style } from './buttonClasses';
import Button from './Button';
import ButtonGroup from './ButtonGroup';
import FilterSelect from './FilterSelect';
import type { SelectOption } from './Select';
import type { Size, UpdateStatus } from './types';

export const OWNER_TEAM_OPTION_VALUE = 'team';

export type UserOption = {
  value: string;
  label: string;
  profilePicture: string | null;
};

export type TeamOption = {
  label: string;
  icon?: ReactNode;
};

export type OwnerFilterSelectProps = {
  userOptions: UserOption[];
  value?: string;
  /** The default/reset value. When provided and value !== defaultValue, shows X mark to clear. */
  defaultValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  includeRemoveOption?: boolean;
  /** Include a "Team" option at the top that represents all team members */
  teamOption?: TeamOption;
  size?: Size;
  disabled?: boolean;
  /** Show the selected label/placeholder text. Default true. */
  showText?: boolean;
  /** Show the dropdown chevron. Default true. */
  showChevron?: boolean;
  bordered?: boolean;
  shape?: 'rounded' | 'pill' | 'square';
  color?: ColorName;
  tone?: Style;
  tooltip?: ReactNode;
  /** Status for async operations: 'updating' shows spinner, 'success' shows checkmark, 'error' shows warning. */
  status?: UpdateStatus;
  /** Custom clear handler. When provided with defaultValue, called instead of onChange(defaultValue). */
  onClear?: () => void;
};

export default function OwnerFilterSelect({
  userOptions,
  value,
  defaultValue,
  onChange,
  placeholder = 'Select owner',
  className,
  includeRemoveOption,
  teamOption,
  size,
  disabled,
  showText,
  showChevron,
  bordered,
  shape,
  color,
  tone,
  tooltip,
  status,
  onClear,
}: OwnerFilterSelectProps) {
  const mappedOptions = userOptions.map((user): SelectOption => ({
    value: user.value,
    label: user.label,
    // UserOption carries the display name in `label`, not in the
    // userTeamName/userEmail/name fields TeamUserAvatar reads for its
    // fallback initials. Map label -> userTeamName so users without a
    // profile picture render initials instead of "?" (POS-2219).
    icon: (
      <TeamUserAvatar
        userTeam={{ userTeamName: user.label, profilePicture: user.profilePicture }}
        className="size-full"
        shape="circle"
      />
    ),
  }));

  let options = mappedOptions;

  if (teamOption) {
    options = [
      {
        value: OWNER_TEAM_OPTION_VALUE,
        label: teamOption.label,
        icon: teamOption.icon ?? <Users.Users01 />,
      },
      ...options,
    ];
  }

  if (includeRemoveOption) {
    options = [{ value: '', label: 'Remove owner', icon: <General.Trash01 /> }, ...options];
  }

  const showClearButton = defaultValue !== undefined && value !== defaultValue;

  const select = (
    <FilterSelect
      icon={<Users.User03 />}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      status={status}
      disabled={disabled}
      autoWidth
      size={size}
      showText={showText}
      showChevron={showClearButton ? false : showChevron}
      bordered={bordered}
      shape={shape}
      color={color}
      tone={tone}
      tooltip={tooltip}
    />
  );

  if (showClearButton) {
    return (
      <ButtonGroup size={size}>
        {select}
        <Button icon={<General.XMark />} onClick={onClear ?? (() => onChange(defaultValue))} />
      </ButtonGroup>
    );
  }

  return select;
}
